/**
 * Page-level scroll-lock manager shared across all widget placements.
 *
 * Fullscreen hides the host page's scrollbar by setting `overflow: hidden` on
 * `<html>`. That target is global, so with more than one placement on a page (see the
 * multi-placement support) a per-instance boolean owner flag races: if placement A locks,
 * then B locks and snapshots A's already-`overflow:hidden` style as its "original", then A
 * exits, B's later exit would re-assert the lock permanently. Reference counting fixes it:
 * the original `<html>` style is captured once on the FIRST acquire and restored once when
 * the LAST owner releases. Release of a token that never acquired is a no-op, so a
 * non-fullscreen placement unmounting can't release another placement's active lock.
 *
 * Tokens are opaque object identities (one per placement instance), so duplicate
 * placement keys can't collide.
 */
const owners = new Set<object>();
let originalStyle: string | null = null;

export const acquireScrollLock = (token: object): void => {
  if (typeof document === "undefined") return;
  if (owners.size === 0) {
    // Capture the host page's original <html> style exactly once, before the first lock.
    originalStyle = document.documentElement.getAttribute("style");
    document.documentElement.setAttribute("style", "overflow: hidden");
  }
  owners.add(token);
};

export const releaseScrollLock = (token: object): void => {
  if (typeof document === "undefined") return;
  // No-op if this token never held the lock (e.g. a placement that never went fullscreen).
  if (!owners.delete(token)) return;
  if (owners.size === 0) {
    if (originalStyle !== null) {
      document.documentElement.setAttribute("style", originalStyle);
    } else {
      document.documentElement.removeAttribute("style");
    }
    originalStyle = null;
  }
};

/** Test-only: reset manager state between cases. */
export const _resetScrollLock = (): void => {
  owners.clear();
  originalStyle = null;
};
